import {toast} from 'react-toastify';
import {QRCodeSVG} from 'qrcode.react';
import {observer} from 'mobx-react-lite';
import React, {FC, useEffect, useState} from 'react';
import {ArrowDownTrayIcon, ShieldCheckIcon} from '@heroicons/react/24/outline';
import {enrollMFA, IMFAEnrollment, verifyMFAEnrollment, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, CopyField, Input, Label} from '../Components';
import {Dialog} from './Dialog';

interface EnrollMFADialogProps {
  onClose: () => void;
  onEnrolled: () => void;
}

const STEP_SUBTITLES = {
  1: 'Step 1: Connect an authenticator app',
  2: 'Step 2: Confirm it works',
  3: 'Step 3: Save your backup codes',
};

/**
 * Scan, prove the app works, save the codes. The order matters: the codes are issued by the verify
 * call, so a mistyped setup never leaves someone holding codes for an enrollment that did not happen.
 */
export const EnrollMFADialog: FC<EnrollMFADialogProps> = observer(({onClose, onEnrolled}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [enrollment, setEnrollment] = useState<IMFAEnrollment | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    enrollMFA(VerdocsEndpoint.getDefault())
      .then(setEnrollment)
      .catch((e) => {
        toast.error(e.response?.data?.error || 'Unable to start setup. Please try again.');
        onClose();
      });
  }, []);

  const handleVerify = () => {
    setWorking(true);
    setError('');
    verifyMFAEnrollment(VerdocsEndpoint.getDefault(), code)
      .then((r) => {
        setWorking(false);
        setBackupCodes(r.backup_codes);
        setStep(3);
      })
      .catch((e) => {
        setWorking(false);
        setCode('');
        setError(e.response?.data?.error || 'That code was not accepted. Please check the value and try again.');
      });
  };

  const handleDownload = () => {
    const blob = new Blob([`Verdocs backup codes\n\n${backupCodes.join('\n')}\n`], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'verdocs-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog onCancel={step === 3 ? () => undefined : onClose}>
      <div className="overflow-y-auto">
        <div className="mb-1 text-lg font-medium text-[#33364b]">Set up two-factor authentication</div>
        <div className="mb-4 text-[13px] text-gray-500">{STEP_SUBTITLES[step]}</div>

        {step === 1 && (
          <>
            <div className="flex flex-col items-start gap-5 tablet:flex-row">
              {enrollment ? (
                <QRCodeSVG
                  value={enrollment.otpauth_url}
                  size={168}
                  className="flex-shrink-0 rounded-md border border-[#ebecf0] bg-white p-2"
                />
              ) : (
                <div className="h-[168px] w-[168px] flex-shrink-0 rounded-md border border-[#ebecf0] bg-[#fbfbfc]" />
              )}
              <div className="text-[13px] text-gray-500">
                <div className="mb-2 text-[#33364b]">Scan this with your authenticator app.</div>
                Google Authenticator, 1Password, Authy, and Microsoft Authenticator are all popular choices.
                <div className="mt-4 mb-1 text-[12px] text-gray-400">Can't scan the code? Enter this key by hand.</div>
                <CopyField label="Setup key" value={enrollment?.secret || ''} />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" label="Cancel" onClick={onClose} />
              <Button label="Next" disabled={!enrollment} onClick={() => setStep(2)} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="mb-4 flex flex-col gap-2">
              <Label>Code from your app</Label>
              <Input
                value={code}
                autoFocus
                inputMode="numeric"
                placeholder="123456"
                className="w-40 font-mono"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              {/* <div className="text-[12px] text-gray-500">Six digits. It changes every 30 seconds, so enter the current one.</div> */}
              {error && <div className="text-[12px] text-[#bb2e3e]">{error}</div>}
            </div>
            <div className="mt-5 flex justify-between gap-2">
              <Button variant="secondary" label="Back" onClick={() => setStep(1)} />
              <Button label="Verify" disabled={working || code.length !== 6} onClick={handleVerify} />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="mb-3 flex items-center gap-2 text-[13px] text-[#1f7a4c]">
              <ShieldCheckIcon className="h-5 w-5" />
              Your authenticator is connected.
            </div>
            <div className="mb-3 text-[13px] text-gray-500">
              If you lose access to your phone or authenticator app, these backup codes may be used instead. Each backup code works only
              once. Store them somewhere safe.
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-1.5 whitespace-nowrap rounded-md border border-[#ebecf0] bg-[#fbfbfc] p-4 font-mono text-[13px] text-[#33364b]">
              {backupCodes.map((backupCode) => (
                <div key={backupCode}>{backupCode}</div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="small"
                variant="secondary"
                label="Download"
                icon={<ArrowDownTrayIcon className="h-4 w-4" />}
                onClick={handleDownload}
              />
              <Button
                size="small"
                variant="secondary"
                label="Copy"
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n')).catch(() => undefined);
                  toast.success('Backup codes copied');
                }}
              />
            </div>
            <label className="mt-4 flex cursor-pointer items-center gap-2 text-[13px] text-[#33364b]">
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />I have saved these somewhere safe
            </label>
            <div className="mt-5 flex justify-end">
              <Button label="Finish" disabled={!saved} onClick={onEnrolled} />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
});
